import { creatureType, type CreatureType } from "./creatures.ts";
import { glbBufferProblems, readGlb, type Glb } from "./glb.ts";

/**
 * Contrato de clipes entre o animador (F41) e o jogo (F49 avatares KayKit, F50 criaturas): os nomes
 * dos clipes que um GLB rigado traz, quais repetem em laço e quais não podem faltar, e a checagem do
 * JSON do glTF exportado. Só dados e JSON: nada de three.js aqui.
 *
 * Convenção do arquivo (o exportador do animador garante, o jogo assume): Y para cima, metros, pés em
 * y = 0, centrado em XZ, de frente para −Z, uma skin só, clipes no lugar (o deslocamento vem do
 * servidor), e o nó raiz da cena com `extras.eldrun` (`EldrunGltfExtras`).
 */
export const CLIP_CONTRACT_VERSION = 1;

export interface ClipSpec {
  readonly name: string;
  /** Repete em laço (locomoção, parado) ou toca uma vez (golpes, dano, morte). */
  readonly loop: boolean;
  readonly required: boolean;
}

/** Prefixo dos clipes de ataque das criaturas: `golpe:<id do ataque>` (`golpe:mordida`). */
export const ATTACK_CLIP_PREFIX = "golpe:";

export const attackClipName = (attackId: string): string => `${ATTACK_CLIP_PREFIX}${attackId}`;

/** Clipes comuns a toda criatura; os golpes vêm dos ataques do tipo (`creatureClipSpec`). */
export const CREATURE_BASE_CLIPS: readonly ClipSpec[] = [
  { name: "parado", loop: true, required: true },
  { name: "andando", loop: true, required: true },
  { name: "correndo", loop: true, required: false },
  { name: "surgindo", loop: false, required: false },
  { name: "dano", loop: false, required: false },
  { name: "morte", loop: false, required: true },
];

/**
 * Clipes de uma criatura: os comuns e um golpe obrigatório por ataque (`CreatureType.attacks[].id`).
 * A fauna sem ataques não tem `golpe:*`.
 */
export function creatureClipSpec(type: CreatureType): ClipSpec[] {
  // Um ataque que reaproveita o clipe de outro (F69) não pede clipe próprio.
  const attackIds = [...new Set(type.attacks.map((attack) => attack.clip ?? attack.id))];
  return [...CREATURE_BASE_CLIPS, ...attackIds.map((id) => ({ name: attackClipName(id), loop: false, required: true }))];
}

/**
 * Clipes do avatar do jogador (personagens KayKit, F49). `pulando` é só o impulso; `no-ar` repete até
 * `aterrissando`. `agachado` é andar agachado e `agachado-parado`, ficar agachado sem andar (opcional,
 * desde o polimento da F49: um arquivo sem ele continua válido, então o contrato segue na versão 1).
 * `arco` é puxar a corda; `revivendo` é levantar depois de reanimado.
 */
export const AVATAR_CLIP_SPEC: readonly ClipSpec[] = [
  { name: "parado", loop: true, required: true },
  { name: "andando", loop: true, required: true },
  { name: "correndo", loop: true, required: true },
  { name: "pulando", loop: false, required: true },
  { name: "no-ar", loop: true, required: true },
  { name: "aterrissando", loop: false, required: true },
  { name: "agachado", loop: true, required: false },
  { name: "agachado-parado", loop: true, required: false },
  { name: "nadando", loop: true, required: false },
  { name: "golpe", loop: false, required: true },
  { name: "arco", loop: false, required: false },
  { name: "comendo", loop: false, required: false },
  { name: "dano", loop: false, required: true },
  { name: "morte", loop: false, required: true },
  { name: "revivendo", loop: false, required: false },
];

/** Para quem o GLB é feito: uma criatura do bestiário ou o avatar do jogador. */
export type EldrunGltfTarget = { kind: "criatura"; creature: CreatureType } | { kind: "avatar" };

/**
 * O que o validador aceita: os alvos acima e o arquivo só com a malha de um personagem (F88, ADR 0018),
 * com a skin e o esqueleto e sem clipe nenhum (os clipes vêm do arquivo do esqueleto, que é um `avatar`
 * sem malha). Só o pipeline de assets e o cliente escrevem e leem esse arquivo; o animador exporta o
 * avatar inteiro.
 */
export type EldrunGlbTarget = EldrunGltfTarget | { kind: "avatar-malha" };

/** Valor de `extras.eldrun.alvo`: o id da criatura, "avatar" ou, no arquivo só de malha, "avatar-malha". */
export const AVATAR_TARGET = "avatar";
export const AVATAR_MESH_TARGET = "avatar-malha";

export const eldrunTargetId = (target: EldrunGlbTarget): string =>
  target.kind === "avatar" ? AVATAR_TARGET : target.kind === "avatar-malha" ? AVATAR_MESH_TARGET : target.creature.id;

export function eldrunClipSpec(target: EldrunGlbTarget): readonly ClipSpec[] {
  return target.kind === "avatar" ? AVATAR_CLIP_SPEC : target.kind === "avatar-malha" ? [] : creatureClipSpec(target.creature);
}

/**
 * `extras.eldrun` do nó raiz da cena (o `userData.eldrun` que o GLTFExporter grava como extras). Nos
 * golpes das criaturas (F50), `impacto` é opcional: o instante (s) do clipe em que o golpe acerta, que
 * o jogo alinha ao acerto do servidor. Opcional, então o contrato continua na versão 1.
 */
export interface EldrunGltfExtras {
  contrato: number;
  alvo: string;
  clips: Record<string, EldrunClipExtras>;
}

export interface EldrunClipExtras {
  loop: boolean;
  impacto?: number;
}

/** O pedaço do JSON do glTF que a checagem lê. */
interface GltfJson {
  scene?: unknown;
  scenes?: unknown;
  nodes?: unknown;
  skins?: unknown;
  animations?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Confere o JSON de um glTF (o chunk JSON do GLB, já parseado) contra o contrato do alvo. Devolve os
 * problemas em pt-BR; vazio = válido. Não lê o buffer binário.
 */
export function validateEldrunGltf(json: unknown, target: EldrunGlbTarget): string[] {
  if (!isRecord(json)) return ["O arquivo não é um glTF: o JSON precisa ser um objeto."];
  const gltf = json as GltfJson;
  const problems: string[] = [];
  const nodes = Array.isArray(gltf.nodes) ? gltf.nodes : [];

  const skins = Array.isArray(gltf.skins) ? gltf.skins.length : 0;
  if (skins !== 1) problems.push(`O arquivo precisa de exatamente uma skin (tem ${skins}).`);

  const rootIndex = sceneRootNode(gltf, problems);
  const root = rootIndex === undefined ? undefined : nodes[rootIndex];
  const expectedTarget = eldrunTargetId(target);
  const extras = rootIndex === undefined ? undefined : readExtras(root, expectedTarget, problems);

  const spec = new Map(eldrunClipSpec(target).map((clip) => [clip.name, clip]));
  const animations = Array.isArray(gltf.animations) ? gltf.animations : [];
  const seen = new Map<string, number>();
  for (const animation of animations) {
    const name = isRecord(animation) && typeof animation["name"] === "string" ? animation["name"] : "";
    seen.set(name, (seen.get(name) ?? 0) + 1);
    if (!spec.has(name)) {
      problems.push(name === "" ? "Há uma animação sem nome." : `Clipe fora do contrato: "${name}".`);
    }
    if (rootIndex !== undefined && movesRootTranslation(animation, rootIndex)) {
      problems.push(`O clipe "${name}" move a translação do nó raiz: os clipes têm de ser no lugar.`);
    }
  }
  for (const [name, count] of seen) {
    if (count > 1 && name !== "") problems.push(`O clipe "${name}" aparece ${count} vezes.`);
  }
  for (const clip of spec.values()) {
    if (clip.required && !seen.has(clip.name)) problems.push(`Falta o clipe obrigatório "${clip.name}".`);
  }

  if (extras !== undefined) {
    for (const name of seen.keys()) {
      if (name !== "" && !Object.hasOwn(extras.clips, name)) problems.push(`extras.eldrun.clips não lista o clipe "${name}".`);
    }
    for (const [name, entry] of Object.entries(extras.clips)) {
      if (!seen.has(name)) {
        problems.push(`extras.eldrun.clips lista "${name}", que não está nas animações.`);
        continue;
      }
      const expected = spec.get(name);
      if (!isRecord(entry) || typeof entry["loop"] !== "boolean") {
        problems.push(`extras.eldrun.clips["${name}"].loop precisa ser true ou false.`);
      } else if (expected !== undefined && entry["loop"] !== expected.loop) {
        problems.push(`extras.eldrun.clips["${name}"].loop deveria ser ${String(expected.loop)}.`);
      }
      if (isRecord(entry) && entry["impacto"] !== undefined) {
        const impact = entry["impacto"];
        if (!name.startsWith(ATTACK_CLIP_PREFIX)) problems.push(`extras.eldrun.clips["${name}"].impacto só vale nos golpes (${ATTACK_CLIP_PREFIX}*).`);
        else if (typeof impact !== "number" || !Number.isFinite(impact) || impact < 0) problems.push(`extras.eldrun.clips["${name}"].impacto precisa ser um número de segundos ≥ 0 (é ${show(impact)}).`);
      }
    }
  }
  return problems;
}

const AVATAR_FILE = /^avatar-[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Alvo de um GLB do pipeline pelo nome do arquivo em `resources/rigged` (sem `.glb`): o id de uma
 * criatura do bestiário ou `avatar-<nome>`. undefined para qualquer outro nome.
 */
export function riggedTarget(id: string): EldrunGltfTarget | undefined {
  if (AVATAR_FILE.test(id)) return { kind: "avatar" };
  const creature = creatureType(id);
  return creature === undefined ? undefined : { kind: "criatura", creature };
}

/**
 * `validateEldrunGltf` a partir dos bytes do GLB, com a integridade do contêiner (`readGlb`,
 * `glbBufferProblems`): o que o exportador do animador, o servidor de
 * desenvolvimento que grava em `resources/rigged` e o pipeline de assets conferem. Vazio = válido.
 */
export function validateEldrunGlb(bytes: Uint8Array, target: EldrunGlbTarget): string[] {
  let glb: Glb;
  try {
    glb = readGlb(bytes);
  } catch (error) {
    return [(error as Error).message];
  }
  return [...glbBufferProblems(glb.json, glb.bin?.byteLength), ...validateEldrunGltf(glb.json, target)];
}

/** Índice do único nó raiz da cena padrão; registra o problema e devolve undefined se não houver um só. */
function sceneRootNode(gltf: GltfJson, problems: string[]): number | undefined {
  const scenes = Array.isArray(gltf.scenes) ? gltf.scenes : [];
  const sceneIndex = typeof gltf.scene === "number" ? gltf.scene : 0;
  const scene: unknown = scenes[sceneIndex];
  if (!isRecord(scene)) {
    problems.push("O arquivo não tem cena.");
    return undefined;
  }
  const roots = Array.isArray(scene["nodes"]) ? scene["nodes"] : [];
  if (roots.length !== 1 || typeof roots[0] !== "number") {
    problems.push(`A cena precisa de exatamente um nó raiz (tem ${roots.length}).`);
    return undefined;
  }
  return roots[0];
}

/**
 * Valor lido do arquivo numa mensagem: escalares como JSON (texto longo cortado); listas e objetos só
 * pelo tipo, porque podem ser enormes ou fundos demais para o `JSON.stringify`.
 */
function show(value: unknown): string {
  if (typeof value === "string") return JSON.stringify(value.length > 60 ? `${value.slice(0, 60)}…` : value);
  if (typeof value === "number" || typeof value === "boolean" || value === null) return String(value);
  return Array.isArray(value) ? "uma lista" : "um objeto";
}

function mismatch(field: string, value: unknown, expected: string): string {
  return value === undefined ? `${field} está ausente; o esperado é ${expected}.` : `${field} é ${show(value)}; o esperado é ${expected}.`;
}

function readExtras(root: unknown, expectedTarget: string, problems: string[]): EldrunGltfExtras | undefined {
  const extras = isRecord(root) && isRecord(root["extras"]) ? root["extras"]["eldrun"] : undefined;
  if (!isRecord(extras)) {
    problems.push("O nó raiz não tem extras.eldrun.");
    return undefined;
  }
  if (extras["contrato"] !== CLIP_CONTRACT_VERSION) {
    problems.push(mismatch("extras.eldrun.contrato", extras["contrato"], String(CLIP_CONTRACT_VERSION)));
  }
  if (extras["alvo"] !== expectedTarget) {
    problems.push(mismatch("extras.eldrun.alvo", extras["alvo"], `"${expectedTarget}"`));
  }
  if (!isRecord(extras["clips"])) {
    problems.push("extras.eldrun.clips precisa ser um objeto { nome: { loop } }.");
    return undefined;
  }
  return { contrato: CLIP_CONTRACT_VERSION, alvo: expectedTarget, clips: extras["clips"] as Record<string, EldrunClipExtras> };
}

function movesRootTranslation(animation: unknown, rootIndex: number): boolean {
  if (!isRecord(animation) || !Array.isArray(animation["channels"])) return false;
  return animation["channels"].some((channel: unknown) => {
    const target = isRecord(channel) ? channel["target"] : undefined;
    return isRecord(target) && target["node"] === rootIndex && target["path"] === "translation";
  });
}
